import moment from "moment";
import Evaluation from "../../models/Evaluation";
import SoundClip from "../../models/SoundClip";
import { Tag, TagSentiment } from "../../models/Tag";
import { ITranscriptionSegment } from "../../services/TranscriptionService";
import { serializeToUtc } from "../../utils/DateTimeUtils";
import { AnswerColor } from "../UI/AcxAnswerSelector";
import { GroupedTags } from "./Stores/EvalStore";
import { InteractionType } from "models/InteractionType";
import { Theme } from "@mui/material";
import hexToRGB from "utils/hexToRGB";
import { AssignedWorkflowInstance } from "models/Workflows/WorkflowInstance";

export function matchTranscriptionToSoundClip(
    soundClip: SoundClip,
    words: ITranscriptionSegment[],
) {
    const startTime = soundClip.startTime;
    const endTime = soundClip.endTime;

    if (startTime === undefined || endTime === undefined) {
        throw new Error(
            "Sound clip must have a start and end time to match transcription.",
        );
    }

    if (words.length === 0) {
        throw new Error("Sound clip must have a transcription to match.");
    }

    if (endTime - startTime < 1) {
        throw new Error(
            "Sound clip must be at least 1 second long to be created.",
        );
    }

    const filteredWords = words.filter(
        (word) => word.endTime > startTime && word.startTime < endTime,
    );

    const res = filteredWords.map((value) => value.text).join(" ");
    if (res.length === 0) {
        throw new Error("Clip must contain audible dialog to be created.");
    }
    soundClip.setTranscriptionText(res);
}

export function prepareEvalForSerialization(
    currentEval: Evaluation,
    userName?: string,
) {
    // @ts-ignore
    delete currentEval.isEditable;
    // @ts-ignore
    delete currentEval.actionThread;

    // @ts-ignore
    delete currentEval.interaction?.audioMetadata;
    // @ts-ignore
    delete currentEval.interaction?.agent;

    // @ts-ignore
    delete currentEval.interaction?.organizationStructureMember;
    // @ts-ignore
    delete currentEval.interaction?.organization;
    // @ts-ignore
    delete currentEval.interaction?.customerType;
    // @ts-ignore
    delete currentEval.organization;
    // @ts-ignore
    delete currentEval.analyst;

    currentEval.workflowInstances?.forEach((value) => {
        const assignedInstance = value as AssignedWorkflowInstance;
        // @ts-ignore
        delete assignedInstance.assignedToUser;
        // @ts-ignore
        delete assignedInstance.questionModuleId;
        // @ts-ignore
        delete assignedInstance.activeLevel;
        // @ts-ignore
        delete assignedInstance.question;
        // @ts-ignore
        delete assignedInstance.workflowCondition;

        // @ts-ignore
        delete assignedInstance.bossEvalModule;

        value.workflowInstanceLevels.forEach((value1) => {
            // @ts-ignore
            delete value1.workflowMessagingMetadata;
            // @ts-ignore
            delete value1.workflowInstance;
            // @ts-ignore
            delete value1.user;
        });
    });

    currentEval.classifierResultValidations.forEach((value) => {
        // @ts-ignore
        delete value.classifier;
        // @ts-ignore
        delete value.evaluation;
    });

    currentEval.soundClips?.forEach((value) => {
        delete value._tags;
        delete value.soundClipAnswers;
    });

    currentEval.soundClips = currentEval.soundClips?.filter(
        (value) => !value.id.includes("temporary-clip"),
    );

    currentEval.answers.forEach((value) => {
        // @ts-ignore
        delete value.question;
        // @ts-ignore
        delete value.evaluationModule;

        delete value.answerDisputes;

        value.answerTags.forEach((value1) => {
            delete value1.tag;
            delete (value1 as any).classifierResult;
        });

        value.predictedTags.forEach((value1) => {
            delete value1.tag;
            delete (value1 as any).classifierResult;
        });

        value.soundClipAnswers.forEach((value1) => {
            delete value1.soundClip;
        });
    });

    currentEval.soundClips = currentEval.soundClips?.filter((value) =>
        Boolean(value.id),
    );

    currentEval.modifiedOn = serializeToUtc(moment().local());
    currentEval.modifiedBy = userName;
}

export function soundClipDurationDisplay(val: number | string) {
    const dur = moment.duration(val, "s");

    return moment.utc(dur.asMilliseconds()).format("m[m]:ss[s]");
}

export function textClipTimestampDisplay(timestamp: string) {
    return moment(timestamp).format("hh:mm:ss");
}

export function generateClipForEvaluation(
    currentEval: Evaluation,
    clipName: string,
    start: number,
    end: number,
    userName: string | undefined,
) {
    const clip = new SoundClip(undefined, userName, userName);

    const url = `https://${
        currentEval!.interaction!.audioMetadata!.directory!.account
    }.blob.core.windows.net/${
        currentEval!.interaction!.audioMetadata!.blobFileKey
    }`;

    clip.segmentName = clipName;
    clip.mediaType = currentEval!.interaction!.interactionType;
    clip.note = "";
    clip.url = url;
    clip.evaluationId = currentEval!.id;
    clip.tags = "";
    clip.startTime = start;
    clip.endTime = end;
    clip.isActive = true;
    clip.audioMetadataId = currentEval!.interaction!.audioMetadata!.id;

    return clip;
}

export function generateClipForConversation(
    audioMetadataId: string,
    evalId: string | undefined,
    clipName: string,
    start: number,
    end: number,
    userName: string | undefined,
    isChat?: boolean,
) {
    const clip = new SoundClip(undefined, userName, userName);

    clip.audioMetadataId = audioMetadataId;
    clip.segmentName = clipName;
    clip.mediaType = isChat
        ? InteractionType.ProcessedChat
        : InteractionType.Audio;
    clip.note = "";
    clip.evaluationId = evalId;
    clip.tags = "";
    clip.startTime = start;
    clip.endTime = end;
    clip.isActive = true;

    return clip;
}

export const groupedTagsFromTags = (tags: Tag[], current?: GroupedTags[]) => {
    let groups: GroupedTags[] = [];
    if (current) {
        groups = current;
    }
    tags.forEach((element) => {
        const idx = groups.findIndex((g) => g.label === element.parent?.value);
        if (idx > -1) {
            groups[idx].options.push(element);
        } else {
            groups.push({
                label: element.parent?.value ?? "Other",
                options: [element],
            });
        }
    });
    return groups;
};

export function sentimentToColor(
    tagSentiment: TagSentiment,
    score: number | null,
) {
    if (score == null) {
        return AnswerColor.BLUE;
    }
    switch (tagSentiment) {
        case TagSentiment.Negative:
            return AnswerColor.RED;
        case TagSentiment.Neutral:
            return AnswerColor.YELLOW;
        case TagSentiment.Positive:
            return AnswerColor.GREEN;
    }
}

export function answerColorToThemeColor(
    theme: Theme,
    answerColor?: AnswerColor,
) {
    switch (answerColor) {
        case AnswerColor.GREEN:
            return {
                color: theme.palette.black.main,
                borderColor: theme.palette.green.main,
                backgroundColor: theme.palette.green.background,
            };
        case AnswerColor.YELLOW:
            return {
                color: theme.palette.black.main,
                borderColor: theme.palette.yellow.main,
                backgroundColor: theme.palette.yellow.background,
            };
        case AnswerColor.RED:
            return {
                color: theme.palette.black.main,
                borderColor: theme.palette.red.main,
                backgroundColor: theme.palette.red.background,
            };
        case AnswerColor.BLUE:
            return {
                color: theme.palette.black.main,
                borderColor: theme.palette.info.main,
                backgroundColor: hexToRGB(theme.palette.info.main, 0.05),
            };
        default:
            return {
                color: "#6B7280",
                borderColor: theme.palette.gray[700],
                backgroundColor: theme.palette.gray[100],
            };
    }
}
